import React, { useMemo, useRef } from 'react';
import { Controller, FieldValues, useFormContext } from 'react-hook-form';
// components
import { FormControlLabel, Grid } from '@mui/material';
import { DeleteButton } from 'components/delete-button/delete-button';
import StyledForm from 'components/styled-form';
import OutlinedInputController from 'controllers/outlined-input-controller';
import { SaveToLibraryCheckbox } from '../components/save-to-library-checkbox';
import SelectionOptions from '../components/selection-options';
// types
import ActionButton from 'components/buttons';
import Checkbox from 'components/checkbox';
import CustomTooltip from 'components/custom-tooltip';
import useToast from 'hooks/useToast';
import { useParams } from 'react-router-dom';
import { useGetCompanyRolePermissionsMeQuery } from 'redux-store/api/company-roles';
import { ISelectionStepProps, TSelectionFormOption } from '../../utils/types';

type TOptions = {
  options: TSelectionFormOption[];
  [x: string]: unknown;
};
interface IProps<T extends FieldValues = TOptions>
  extends Omit<ISelectionStepProps, 'handleSubmit'> {
  isProjectSelection?: boolean;
  isSelectionLibrary?: boolean;
  isEdit?: boolean;
  projectId?: number;
  isDescriptionEditDisable?: boolean;
  renderCostCodeAndLocation?: () => React.ReactElement;
  handleSubmit: (formValues: T) => void;
}

export const GeneralInfo = <T extends FieldValues = TOptions>({
  renderCostCodeAndLocation,
  renderActionButtons,
  handleSubmit,
  isProjectSelection = true,
  isSelectionLibrary = false,
  isEdit,
  projectId: projectIdProp,
  isSelectionApproved = false,
  isDescriptionEditDisable = false,
}: IProps<T>) => {
  const { projectId: projectIdFromParams } = useParams();
  const resolvedProjectId = projectIdProp || projectIdFromParams;
  const projectId = Number(resolvedProjectId);

  const parentRef = useRef<{ resetOptions: () => void; addOption: () => void }>(null);

  const { data: userPermissions } = useGetCompanyRolePermissionsMeQuery();

  const showToaster = useToast();

  const form = useFormContext<FieldValues>();

  const formOptions = form.watch('options');

  const showOptionName = useMemo(() => formOptions.length > 1, [formOptions.length]);

  const onResetOptions = () => {
    parentRef.current?.resetOptions();
  };

  const onAddOption = () => {
    parentRef.current?.addOption();
  };

  const withInfoHandlingSubmit = (formValues: T) => {
    const uploadedFiles = formValues?.options?.flatMap(({ uploads }) => uploads);

    const hasPendingUploads = uploadedFiles?.some(
      (file) => !['done', 'error_file_size'].includes(file?.status)
    );

    if (hasPendingUploads) {
      showToaster({
        message: 'Uploads are still in progress. Please wait until they are fully uploaded.',
        type: 'info',
      });
      return;
    }

    handleSubmit(formValues);
  };

  const errors = form.formState.errors;

  const optionsErrors = errors?.options;

  const isAddButtonDisabled =
    isSelectionApproved || (Array.isArray(optionsErrors) && optionsErrors.some((item) => !!item));

  const tooltipTitle =
    isAddButtonDisabled && !isSelectionApproved
      ? 'Please fill in all required fields before adding another option.'
      : '';

  return (
    <StyledForm sx={styles.form} onSubmit={form.handleSubmit(withInfoHandlingSubmit)}>
      <Grid sx={styles.content}>
        {renderCostCodeAndLocation ? renderCostCodeAndLocation() : null}

        {showOptionName && (
          <OutlinedInputController
            fullWidth
            withError
            name="title"
            label="Option Name"
            placeholder=""
            disabled={isSelectionApproved}
          />
        )}

        <SelectionOptions
          hasCostCodeAndLocation={!!renderCostCodeAndLocation}
          projectId={projectId}
          isProjectSelection={isProjectSelection}
          isSelectionApproved={isSelectionApproved}
          isDescriptionEditDisable={isDescriptionEditDisable}
          parentRef={parentRef}
        />

        {isProjectSelection && !isEdit && userPermissions?.LIBRARY_SELECTIONS?.canCreate && (
          <SaveToLibraryCheckbox />
        )}
      </Grid>

      <Grid sx={styles.footer}>
        <CustomTooltip placement="top" title={tooltipTitle}>
          <Grid sx={styles.addOptionBtn}>
            <ActionButton
              disabled={isAddButtonDisabled}
              type="button"
              label="Add New Option"
              variant="secondary"
              onClick={onAddOption}
            />
          </Grid>
        </CustomTooltip>

        <Grid sx={styles.actionBtnsContainer}>
          {isEdit && !isProjectSelection && isSelectionLibrary && (
            <Grid sx={styles.updateOption}>
              <Controller
                name="updateTemplates"
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ p: 0, m: 0 }}
                    labelPlacement="start"
                    label="Update templates"
                    control={
                      <Checkbox
                        sx={{ p: 1 }}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="primary"
                      />
                    }
                  />
                )}
              />
            </Grid>
          )}
          <Grid sx={styles.actionButtons}>
            <Grid sx={styles.deleteItemBtn}>
              {!isSelectionApproved && <DeleteButton text="Delete item" onClick={onResetOptions} />}
            </Grid>
            {renderActionButtons()}
          </Grid>
        </Grid>
      </Grid>
    </StyledForm>
  );
};

const styles = {
  form: {
    height: '100%',
    overflow: 'hidden',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    overflowY: 'auto',
    padding: '0 40px',
    '&::-webkit-scrollbar': {
      border: 'none',
      width: '11px',
      height: '11px',
    },

    '&::-webkit-scrollbar-track': {
      backgroundColor: '#fff',
      border: 'none',
      height: '10px',
    },

    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#D4D6E0',
      width: '2px',
      borderRadius: '6px',
      cursor: 'pointer',
    },
  },
  footer: {
    padding: '0 40px',
  },
  addOptionBtn: {
    '& button': {
      minWidth: '100%',
      borderRadius: '12px',
    },
  },
  actionBtnsContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '20px',
  },
  updateOption: { display: 'flex', flexDirection: 'row' },
  actionButtons: { display: 'flex', flexDirection: 'row', alignItems: 'center' },
  deleteItemBtn: {
    flex: 1,
  },
};
